import Fetch from '@/utils/Fetch';
import Storage from '@/utils/storage';
import * as Enums from '@/utils/enums';
import UCS from '@/services/option/user-config-service';

/**
 * The onboarding skips live in the signed-in user's dashboard config (AuthUserConfiguration,
 * Dashboard section), so they follow the user across browsers and devices:
 * - MetaData.OnboardingLogoSkipped: step 2 "No logo yet" on the logo upload.
 * (Step 1 can't be skipped: it stays open until someone else is invited.)
 */

export interface OnboardingSkips {
    logo: boolean;
}
export type OnboardingSkip = keyof OnboardingSkips;

// UCS.getSettings swallows failures into a blank config. Saving that blank config (it has no ID)
// would insert a second Dashboard row, so a failed read throws here instead. (Fetch resolves 4xx/5xx
// as { status }, and a 401 as an empty list with ResponseStatus.)
const failed = (response: any) => !response || response.status >= 400 || response.ResponseStatus >= 400;

const fetchDashboardConfig = async (): Promise<any | null> => {
    const response: any = await Fetch.get({
        url: '/AuthUserConfiguration',
        params: { type: Enums.ConfigurationType.PageFilters, section: Enums.ConfigurationSection.Dashboard },
    });
    if (failed(response) || !Array.isArray(response.Results)) throw new Error('dashboard config request failed');
    return response.Results[0] ?? null;
};

export const loadOnboardingSkips = async (): Promise<OnboardingSkips> => {
    const config = await fetchDashboardConfig();
    return { logo: UCS.getOnboardingLogoSkipped(config) };
};

// Saves run one at a time: each re-reads the config, so two overlapping saves would otherwise
// both start from the same copy and the second would drop the first's change.
let saveQueue: Promise<unknown> = Promise.resolve();

export const saveOnboardingSkip = (skip: OnboardingSkip, skipped: boolean): Promise<void> => {
    const save = saveQueue.then(() => writeOnboardingSkip(skip, skipped));
    saveQueue = save.catch(() => undefined);
    return save;
};

const writeOnboardingSkip = async (skip: OnboardingSkip, skipped: boolean): Promise<void> => {
    // Re-read right before writing: the dashboard layout keeps its widgets in the same MetaData.
    const config = (await fetchDashboardConfig()) ?? {
        AuthUserID: Storage.getCookie(Enums.Cookie.userID),
        ConfigurationType: Enums.ConfigurationType.PageFilters,
        ConfigurationSection: Enums.ConfigurationSection.Dashboard,
        MetaData: JSON.stringify({}),
        IsActive: true,
    };
    if (skip === 'logo') UCS.setOnboardingLogoSkipped(config, skipped);
    const result: any = await UCS.saveConfig(config);
    if (failed(result)) throw new Error('dashboard config save failed');
};
